import { useState } from 'react';
import type { Profile as ProfileData } from '../types';
import { saveProfile } from '../data/profile';
import { phoneView } from '../lib/format';
import { LockIcon } from '../icons';
import { useToast } from '../lib/toast';
import { confirmEmailCode, requestEmailCode, startDeletion } from '../lib/account';

/* ПРОФИЛЬ. Три поля ФИО настоящие: читаются из `read/personal`, пишутся в
   `write/personal` (user-api). Всё остальное в макете источника не имеет — см.
   ниже, каждое со своей причиной.

   ЗАЧЕМ ЭТОТ ЭКРАН ВООБЩЕ НУЖЕН, а не «потом»: вход у пациента по номеру, и
   Keycloak в claims имени не приносит. Без имени сервер не принимает отзыв о
   враче (`400`, «заполните профиль»), а врач в заявке видит пустое ФИО. То есть
   это не украшение кабинета, а единственное место, где пациент может исправить
   то, что иначе исправить нечем.

   ТЕЛЕФОН ТОЛЬКО ЧИТАЕТСЯ, и замок рядом — не заглушка на будущее. Для пациента
   `phoneNumber` в Keycloak это ЛОГИН: по нему его находит телефонный плагин.
   Сервер смену номера через профиль отбивает (403), и правильно: она означала бы
   передачу аккаунта владельцу нового номера. Менять номер — отдельный флоу с
   подтверждением обоих номеров, и его нет.

   ПОЧТА — СВОЙ РАЗДЕЛ, на месте поля рассылки из макета (решение человека 23.10):
   одно поле с адресом, и тот же адрес — и для писем, и для входа. Правится НЕ
   полем формы, а КОДОМ (lib/account.ts): почта — способ входа, и новый адрес
   обязан быть подтверждён кодом, пришедшим на него; `write/personal` почту
   отбивает (403). Всё происходит здесь же, без ухода со страницы.

   УВЕДОМЛЕНИЙ НЕТ (решение человека 23.10): ручки не было ни одной, а выключенный
   переключатель обещал то, чего кабинет не умеет. */

export function Profile({
    initial,
    onSaved,
    onEmailChanged,
}: {
    initial: ProfileData;
    /** Сервер принял ФИО: оболочка кладёт его себе, и «сохранить» гаснет.
        Через проп, а не событием на `window`: событие — это связь, которой не
        видно в типах, и следующий читатель не найдёт, кто его слушает. */
    onSaved: (p: Pick<ProfileData, 'surname' | 'name' | 'fathername'>) => void;
    /** Новый адрес подтверждён и записан: оболочка кладёт его себе. */
    onEmailChanged: (email: string) => void;
}) {
    const toast = useToast();
    const [surname, setSurname] = useState(initial.surname);
    const [name, setName] = useState(initial.name);
    const [fathername, setFathername] = useState(initial.fathername);
    const [busy, setBusy] = useState(false);
    /* «Сохранить» появляется, только когда ФИО правда изменено (решение человека
       23.10: серая кнопка под формой читалась как сломанная). Сравнение с
       ПРИШЕДШИМИ значениями, а не флаг «трогали поле»: пациент мог поправить
       букву и вернуть её обратно, и кнопка не должна обещать изменение. */
    const dirty =
        surname.trim() !== initial.surname ||
        name.trim() !== initial.name ||
        fathername.trim() !== initial.fathername;
    /* Фамилия и имя обязательны: из них сервер собирает подпись отзыва («Анна
       П.»), и пустое имя вернуло бы тот же отказ, из-за которого экран и нужен.
       Отчество необязательно — в `main.user` его почти никогда нет. */
    const ready = surname.trim().length > 1 && name.trim().length > 1;

    async function save() {
        if (!dirty || !ready || busy) return;
        setBusy(true);
        const res = await saveProfile({ surname, name, fathername });
        setBusy(false);
        toast(res.ok ? 'данные сохранены' : res.message);
        // Профиль НЕ перечитывается: сервер принял ровно эти значения, и второй
        // запрос ничего не уточнит. Оболочке отдаём их напрямую — от этого гаснет
        // кнопка и меняется подпись в боковом меню.
        if (res.ok) onSaved({ surname: surname.trim(), name: name.trim(), fathername: fathername.trim() });
    }

    return (
        <>
            <div className="mh">
                <div>
                    <h1>Профиль</h1>
                    <div className="sub">данные, по которым врач находит вашу запись</div>
                </div>
            </div>

            <div className="cols">
                <div>
                    <div className="sec">
                        <h2>Личные данные</h2>
                    </div>
                    <div className="fields">
                        <Field label="Фамилия" value={surname} onChange={setSurname} placeholder="Котова" />
                        <Field label="Имя" value={name} onChange={setName} placeholder="Мария" />
                        <Field
                            label="Отчество"
                            value={fathername}
                            onChange={setFathername}
                            placeholder="Сергеевна"
                        />
                        <div className="fr">
                            <span className="fk">Телефон</span>
                            <span className="fv lock">
                                {initial.phone ? phoneView(initial.phone) : 'не указан'}
                                <LockIcon />
                            </span>
                        </div>
                    </div>
                    {dirty && (
                        <button
                            type="button"
                            className="wide pri"
                            style={{ maxWidth: 280 }}
                            disabled={!ready || busy}
                            onClick={save}
                        >
                            {busy ? 'сохраняем…' : 'сохранить изменения'}
                        </button>
                    )}

                    <EmailSection email={initial.email} onChanged={onEmailChanged} />

                    <p className="fine">
                        Телефон менять здесь нельзя: это логин от кабинета. Новую почту нужно подтвердить
                        кодом из письма, которое придёт на неё. Имя и фамилия видны врачу в заявке на приём
                        и в подписи к отзыву.
                    </p>

                    <DeleteAccount />
                </div>
            </div>
        </>
    );
}

/* РАЗДЕЛ ПОЧТЫ. Поле показывает текущий адрес; вписан другой — «Отправить» шлёт
   на него код, и раздел превращается в ввод кода. Почта в аккаунте меняется
   ТОЛЬКО когда код совпал; до этого «изменить адрес» и уход со страницы ничего
   не оставляют. */
type EmailStep = { kind: 'enter' } | { kind: 'code'; email: string; minutes: number };

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function EmailSection({ email, onChanged }: { email: string; onChanged: (email: string) => void }) {
    const toast = useToast();
    const [step, setStep] = useState<EmailStep>({ kind: 'enter' });
    const [draft, setDraft] = useState(email);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // Отправлять есть что, только если вписан ДРУГОЙ адрес: свой и так подтверждён.
    const changed = draft.trim().toLowerCase() !== email.toLowerCase();
    const draftOk = EMAIL.test(draft.trim()) && changed;

    async function send(address: string) {
        setBusy(true);
        setError('');
        const res = await requestEmailCode(address);
        setBusy(false);
        if (!res.ok) {
            setError(res.message);
            return;
        }
        setCode('');
        setStep({ kind: 'code', email: address.trim(), minutes: Math.max(1, Math.round(res.expiresIn / 60)) });
    }

    async function confirm() {
        if (step.kind !== 'code') return;
        setBusy(true);
        setError('');
        const res = await confirmEmailCode(code);
        setBusy(false);
        if (res.ok) {
            const confirmed = res.email || step.email;
            onChanged(confirmed);
            setDraft(confirmed);
            setCode('');
            setStep({ kind: 'enter' });
            toast('почта изменена');
            return;
        }
        setError(res.message);
        // Код больше не сработает — назад к адресу, а не к пустому полю кода.
        if (res.restart) {
            setCode('');
            setStep({ kind: 'enter' });
        }
    }

    function back() {
        setCode('');
        setError('');
        setStep({ kind: 'enter' });
    }

    return (
        <>
            <div className="sec gap" style={{ paddingBottom: 0 }}>
                <h2>Электронная почта</h2>
            </div>
            <p className="subs">
                {step.kind === 'code'
                    ? `Отправили код на ${step.email}. Он действует ${step.minutes} мин.`
                    : email
                      ? 'Для писем от Aventrae и выгодных предложений. Новый адрес подтверждается кодом из письма.'
                      : 'Укажите почту для писем от Aventrae и выгодных предложений — придёт код подтверждения.'}
            </p>
            <div className="subrow">
                <label className="subf">
                    {step.kind === 'enter' ? (
                        <input
                            type="email"
                            autoComplete="email"
                            placeholder="Электронная почта"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                        />
                    ) : (
                        <input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="Код из письма"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        />
                    )}
                </label>
                {step.kind === 'enter' ? (
                    <button type="button" className="subb" disabled={!draftOk || busy} onClick={() => send(draft)}>
                        {busy ? 'отправляем…' : 'Отправить'}
                    </button>
                ) : (
                    <button type="button" className="subb" disabled={code.length < 4 || busy} onClick={confirm}>
                        {busy ? 'проверяем…' : 'Подтвердить'}
                    </button>
                )}
            </div>
            {error !== '' && <p className="gerr">{error}</p>}
            {step.kind === 'code' && (
                <div className="subrow">
                    <button type="button" className="wide" disabled={busy} onClick={() => send(step.email)}>
                        отправить код ещё раз
                    </button>
                    <button type="button" className="wide" disabled={busy} onClick={back}>
                        изменить адрес
                    </button>
                </div>
            )}
        </>
    );
}

/* УДАЛЕНИЕ АККАУНТА. Удаляет Keycloak (lib/account.ts): здесь только вход в его
   действие. Два шага на нашей стороне — случайный клик не уводит со страницы, — и
   третий у Keycloak: код из СМС и его «удалить аккаунт?». */
function DeleteAccount() {
    const [asking, setAsking] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    return (
        <>
            <div className="sec gap">
                <h2>Удаление аккаунта</h2>
            </div>
            {!asking ? (
                <button type="button" className="wide" style={{ maxWidth: 280 }} onClick={() => setAsking(true)}>
                    удалить аккаунт
                </button>
            ) : (
                <>
                    <p className="subs">
                        Аккаунт удалится навсегда: войти по этому номеру будет нельзя, планы ухода и визиты
                        пропадут из кабинета. Для подтверждения придёт код в СМС.
                    </p>
                    <div className="subrow" style={{ maxWidth: 420 }}>
                        <button
                            type="button"
                            className="wide pri"
                            disabled={busy}
                            onClick={async () => {
                                setBusy(true);
                                setError('');
                                const res = await startDeletion();
                                // Успех — уход на страницу Keycloak, сюда управление не вернётся.
                                setBusy(false);
                                setError(res.message);
                            }}
                        >
                            {busy ? 'открываем…' : 'да, удалить навсегда'}
                        </button>
                        <button type="button" className="wide" disabled={busy} onClick={() => setAsking(false)}>
                            отмена
                        </button>
                    </div>
                    {error !== '' && <p className="gerr">{error}</p>}
                </>
            )}
        </>
    );
}

function Field({
    label,
    value,
    onChange,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
}) {
    return (
        <div className="fr">
            <span className="fk">{label}</span>
            <label className="fin">
                <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
            </label>
        </div>
    );
}
